import { useEffect, useRef } from 'react';
import { CalendarClock, X } from 'lucide-react';

import { usePiegeFocus } from '../hooks/usePiegeFocus';
import type { Etat } from './Rapports';
import type { PageVierges, RapportVierge } from '../api/rapports';

/**
 * « Créer un nouveau rapport » : on ne crée pas un compte rendu dans le vide.
 *
 * Un compte rendu rend compte d'UNE intervention terminée : la modale demande
 * donc laquelle, parmi celles que le SERVEUR propose (liste, échéances et retards
 * viennent de lui). Sans intervention éligible, la modale le dit au lieu
 * d'afficher un formulaire qui ne pourrait pas aboutir.
 */
export function ModaleChoixIntervention({
  ouvert,
  page,
  onFermer,
  onChoisir,
}: {
  readonly ouvert: boolean;
  /** Les interventions à compléter, telles que le serveur les sert. */
  readonly page?: Etat<PageVierges>;
  readonly onFermer: () => void;
  readonly onChoisir: (vierge: RapportVierge) => void;
}) {
  const boite = useRef<HTMLDivElement>(null);
  const premierBouton = useRef<HTMLButtonElement>(null);
  usePiegeFocus(boite, ouvert);

  useEffect(() => {
    if (ouvert) premierBouton.current?.focus();
  }, [ouvert]);

  useEffect(() => {
    if (!ouvert) return;
    const surTouche = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') onFermer();
    };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, [ouvert, onFermer]);

  if (!ouvert) return null;

  const items = page?.donnees?.items ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={(evenement) => {
        if (evenement.target === evenement.currentTarget) onFermer();
      }}
    >
      <div
        ref={boite}
        role="dialog"
        aria-modal="true"
        aria-labelledby="titre-modale-choix"
        className="w-full max-w-xl bg-white rounded-xl shadow-xl border border-slate-200"
      >
        <div className="flex items-start justify-between gap-4 p-5 border-b border-slate-200">
          <div>
            <h2 id="titre-modale-choix" className="text-base font-semibold text-slate-900">
              Créer un compte rendu
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Choisissez l’intervention terminée à couvrir — un compte rendu rend compte d’une
              intervention.
            </p>
          </div>
          <button
            type="button"
            onClick={onFermer}
            className="p-1.5 text-slate-500 hover:text-slate-800 rounded-md"
          >
            <X className="w-4 h-4" aria-hidden="true" />
            <span className="sr-only">Fermer</span>
          </button>
        </div>

        <div className="p-5">
          {page?.erreur && (
            <p
              role="alert"
              className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg p-3"
            >
              {page.erreur}
            </p>
          )}

          {!page?.erreur && items.length === 0 && (
            <p className="text-sm text-slate-600">
              Aucune intervention n’attend de compte rendu : tout ce qui est terminé a déjà le sien.
              Un compte rendu ne peut être ouvert que sur une intervention terminée.
            </p>
          )}

          {items.length > 0 && (
            <ul className="space-y-2">
              {items.map((vierge) => (
                <li key={vierge.interventionId}>
                  <button
                    ref={vierge === items[0] ? premierBouton : undefined}
                    type="button"
                    onClick={() => onChoisir(vierge)}
                    className="w-full text-left px-4 py-3 bg-white border border-slate-200 rounded-lg hover:border-red-300 hover:bg-red-50/40"
                  >
                    <span className="flex items-center justify-between gap-3">
                      <span className="font-medium text-slate-800">{vierge.client}</span>
                      <span
                        className={`spill ${vierge.enRetard ? 'spill-danger' : 'spill-surveil'}`}
                      >
                        <CalendarClock className="w-3 h-3" aria-hidden="true" />
                        {vierge.echeanceLisible}
                      </span>
                    </span>
                    <span className="block text-xs text-slate-500 mt-1">
                      {vierge.typeIntervention} · {vierge.lieu || 'Lieu à préciser'} · intervention
                      du {vierge.dateLisible}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-slate-200">
          <button
            type="button"
            onClick={onFermer}
            className="px-3 py-2 text-sm text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}
